import { useSearchParams } from "react-router";
import type { SortDirection, SortOption, SortState } from "./types";

type SortConfig<TSort extends string> = {
    key: string;
    /** The param the direction is kept in, used only when it is not the sort's default. */
    directionKey: string;
    options: readonly SortOption<TSort>[];
    fallback: TSort;
};

/**
 * Reads the sort a list is under from URL params, falling back to the list's default. useSortParams reads it
 * this way for a component; call this directly where hooks can't run, such as a route loader prefetching the list.
 */
export function parseSortParams<TSort extends string>(
    sortConfig: SortConfig<TSort>,
    searchParams: URLSearchParams
): { by: TSort; direction: SortDirection } {
    const sortInUrl = searchParams.get(sortConfig.key);
    let activeOption = sortConfig.options.find(option => option.value === sortInUrl);
    // A URL with no sort, or one this list does not offer, falls back to the list's default.
    if (activeOption === undefined) {
        activeOption = sortConfig.options.find(option => option.value === sortConfig.fallback);
    }
    if (activeOption === undefined) {
        throw new Error(`The sort fallback "${sortConfig.fallback}" is not one of this list's sort options`);
    }

    // A direction in the URL only overrides the sort's own; anything else falls back.
    const directionInUrl = searchParams.get(sortConfig.directionKey);
    let direction = activeOption.direction;
    if (directionInUrl === "asc" || directionInUrl === "desc") {
        direction = directionInUrl;
    }

    return { by: activeOption.value, direction };
}

/** The sort a list keeps in the URL, falling back to the list's default. */
export function useSortParams<TSort extends string>(sortConfig: SortConfig<TSort>): SortState<TSort> {
    const [searchParams, setSearchParams] = useSearchParams();

    const { by, direction } = parseSortParams(sortConfig, searchParams);

    const setSort = (next: { by: TSort; direction?: SortDirection }) => {
        setSearchParams(
            prev => {
                const params = new URLSearchParams(prev);
                params.set(sortConfig.key, next.by);
                // Only a flipped direction is worth carrying in the URL.
                const chosen = sortConfig.options.find(option => option.value === next.by);
                if (next.direction === undefined || next.direction === chosen?.direction) {
                    params.delete(sortConfig.directionKey);
                } else {
                    params.set(sortConfig.directionKey, next.direction);
                }
                return params;
            },
            { replace: true }
        );
    };

    return { by, direction, set: setSort, options: sortConfig.options };
}
